import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  defer,
  from,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  shareReplay,
  take,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
const REFRESH_TIME_MS = 30 * 1000;
const LOGOUT_TIME_MS = 180 * 60 * 1000; // 3 hour in milliseconds
const KEYCLOAK_EXPIRY_SKEW_MS = 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenRefreshSubscription: Subscription | null = null;
  private refreshInFlight$: Observable<void> | null = null;
  private authenticationInFlight: Promise<boolean> | null = null;
  private readonly sessionChangesSubject = new Subject<void>();
  private sessionVersion = 0;
  private sessionFingerprint: string | null = null;

  readonly sessionChanges$ = this.sessionChangesSubject.asObservable();

  getIssuer(hostname: string): string {
    if (hostname === 'investors.join.capital') {
      return 'https://auth.investors.join.capital/auth/realms/ace';
    } else if (hostname === 'investors.robincap.com') {
      return 'https://auth.investors.robincap.com/auth/realms/ace';
    } else if (hostname === 'investors.interfacecap.com') {
      return 'https://auth.investors.interfacecap.com/auth/realms/ace';
    } else if (hostname === 'investors.bfc.vc') {
      return 'https://auth.investors.bfc.vc/auth/realms/ace';
    } else if (hostname === 'investors.w3.fund') {
      return 'https://auth.investors.w3.fund/auth/realms/ace';
    } else if (hostname === 'investors.vireo.vc') {
      return 'https://auth.investors.vireo.vc/auth/realms/ace';
    } else if (hostname === 'hic-investors.redstone.vc') {
      return 'https://auth.hic-investors.redstone.vc/auth/realms/ace';
    } else if (hostname === 'investors.vanagon.vc') {
      return 'https://auth.investors.vanagon.vc/auth/realms/ace';
    } else if (hostname === 'investors.4p.capital') {
      return 'https://auth.investors.4p.capital/auth/realms/ace';
    } else {
      // Default issuer
      return environment.authUrl;
    }
  }

  getTokenEndpoint(hostname: string): string {
    if (hostname === 'investors.join.capital') {
      return 'https://auth.investors.join.capital/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.robincap.com') {
      return 'https://auth.investors.robincap.com/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.interfacecap.com') {
      return 'https://auth.investors.interfacecap.com/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.bfc.vc') {
      return 'https://auth.investors.bfc.vc/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.w3.fund') {
      return 'https://auth.investors.w3.fund/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.vireo.vc') {
      return 'https://auth.investors.vireo.vc/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'hic-investors.redstone.vc') {
      return 'https://auth.hic-investors.redstone.vc/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.vanagon.vc') {
      return 'https://auth.investors.vanagon.vc/auth/realms/ace/protocol/openid-connect/token';
    } else if (hostname === 'investors.4p.capital') {
      return 'https://auth.investors.4p.capital/auth/realms/ace/protocol/openid-connect/token';
    } else {
      // Default token endpoint
      return environment.tokenEndpoint;
    }
  }

  constructor(private oauthService: OAuthService) {
    const actualDomainName = window.location.hostname;

    const authConfig: AuthConfig = {
      issuer: this.getIssuer(actualDomainName),
      redirectUri: window.location.origin,
      clientId: environment.clientId,
      responseType: 'code',
      showDebugInformation: !environment.production,
      sessionChecksEnabled: false,
      useSilentRefresh: false,
      requireHttps: environment.production,
      tokenEndpoint: this.getTokenEndpoint(actualDomainName),
    };

    this.oauthService.configure(authConfig);
    this.sessionFingerprint = this.getSessionFingerprint();

    this.oauthService.events.subscribe(event => {
      if (event.type === 'token_received') {
        const fingerprint = this.getSessionFingerprint();
        if (
          fingerprint &&
          this.sessionFingerprint &&
          fingerprint !== this.sessionFingerprint
        ) {
          this.markSessionChanged();
        }
        if (fingerprint) this.sessionFingerprint = fingerprint;
      } else if (event.type === 'logout') {
        this.sessionFingerprint = null;
        this.markSessionChanged();
      } else if (
        event.type === 'session_terminated' ||
        event.type === 'session_error' ||
        event.type === 'code_error'
      ) {
        this.sessionFingerprint = null;
        this.markSessionChanged();
        this.oauthService.revokeTokenAndLogout(
          {
            client_id: this.oauthService.clientId,
            returnTo: this.oauthService.redirectUri,
          },
          true
        );
      } else if (event.type === 'token_expires') {
        this.refreshAndAuthenticateIfNeeded();
      }
    });
  }

  logoutAfterThreeHours() {
    const logoutTimer$ = timer(LOGOUT_TIME_MS);
    logoutTimer$.pipe(take(1)).subscribe(() => {
      this.stopTokenRefreshTimer();
      this.sessionFingerprint = null;
      this.markSessionChanged();
      this.oauthService.revokeTokenAndLogout(
        {
          client_id: this.oauthService.clientId,
          returnTo: this.oauthService.redirectUri,
        },
        true
      );
    });
  }

  startTokenRefreshTimer(): void {
    this.stopTokenRefreshTimer();
    this.tokenRefreshSubscription = timer(0, REFRESH_TIME_MS)
      .pipe(
        filter(() => this.hasRefreshableSession()),
        exhaustMap(() =>
          defer(() => this.refreshAccessTokenIfNeeded()).pipe(
            catchError(error => {
              this.handleRefreshFailure(error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  }

  stopTokenRefreshTimer(): void {
    this.tokenRefreshSubscription?.unsubscribe();
    this.tokenRefreshSubscription = null;
  }

  authenticate(): Promise<boolean> {
    if (!this.oauthService.hasValidAccessToken()) {
      this.rememberRequestedRoute();
    }
    return this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then(() => this.oauthService.hasValidAccessToken())
      .catch(error => {
        throw new Error(error);
      });
  }

  isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public logout() {
    this.stopTokenRefreshTimer();
    this.sessionFingerprint = null;
    this.markSessionChanged();
    localStorage.removeItem('tpKey');
    this.oauthService.revokeTokenAndLogout(
      {
        client_id: this.oauthService.clientId,
        returnTo: this.oauthService.redirectUri,
      },
      true
    );
  }

  login() {
    this.oauthService.initLoginFlow();
  }

  public getBearerToken(): string {
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken) {
      throw new Error('Access token not found');
    }
    return `Bearer ${accessToken}`;
  }

  public getFreshBearerToken(): Observable<string> {
    return this.refreshAccessTokenIfNeeded().pipe(
      catchError(error => {
        this.handleRefreshFailure(error);
        return throwError(() => error);
      }),
      map(() => this.getBearerToken())
    );
  }

  public forceRefreshBearerToken(): Observable<string> {
    return this.refreshAccessTokenIfNeeded(true).pipe(
      catchError(error => {
        this.handleRefreshFailure(error);
        return throwError(() => error);
      }),
      map(() => this.getBearerToken())
    );
  }

  private refreshAccessTokenIfNeeded(force = false): Observable<void> {
    const expiresAt = this.oauthService.getAccessTokenExpiration();
    if (
      !force &&
      this.oauthService.hasValidAccessToken() &&
      expiresAt - Date.now() > KEYCLOAK_EXPIRY_SKEW_MS
    ) {
      return of(void 0);
    }

    if (!this.oauthService.getRefreshToken()) {
      return throwError(() => new Error('Refresh token not available'));
    }

    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = defer(() =>
        from(this.oauthService.refreshToken())
      ).pipe(
        map(() => void 0),
        finalize(() => (this.refreshInFlight$ = null)),
        shareReplay(1)
      );
    }
    return this.refreshInFlight$;
  }

  private hasRefreshableSession(): boolean {
    return (
      this.oauthService.hasValidAccessToken() ||
      !!this.oauthService.getRefreshToken()
    );
  }

  private refreshAndAuthenticateIfNeeded(): void {
    this.getFreshBearerToken().subscribe({
      error: () => undefined,
    });
  }

  private handleRefreshFailure(error: unknown): void {
    if (this.isTerminalRefreshError(error)) {
      this.authenticateIfNeeded();
    }
  }

  private isTerminalRefreshError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      const code = this.getOAuthErrorCode(error.error);
      if (this.isTransientOAuthError(code)) {
        return false;
      }
      return error.status === 400 || error.status === 401;
    }

    if (error instanceof Error) {
      return error.message === 'Refresh token not available';
    }

    if (typeof error === 'object' && error !== null) {
      const candidate = error as {
        status?: unknown;
        error?: unknown;
        error_description?: unknown;
      };
      const code = this.getOAuthErrorCode(candidate.error ?? candidate);
      if (this.isTransientOAuthError(code)) {
        return false;
      }
      return (
        candidate.status === 400 ||
        candidate.status === 401 ||
        code === 'invalid_grant'
      );
    }

    return false;
  }

  private getOAuthErrorCode(error: unknown): string | null {
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const candidate = error as {
        error?: unknown;
        error_description?: unknown;
      };
      if (typeof candidate.error === 'string') {
        return candidate.error;
      }
      if (typeof candidate.error_description === 'string') {
        return candidate.error_description;
      }
    }
    return null;
  }

  private isTransientOAuthError(code: string | null): boolean {
    return code === 'temporarily_unavailable' || code === 'server_error';
  }

  private rememberRequestedRoute(): void {
    const requestedRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (requestedRoute.length > 1) {
      localStorage.setItem('tpKey', requestedRoute);
    }
  }

  private authenticateIfNeeded(): void {
    if (this.authenticationInFlight) {
      return;
    }

    this.authenticationInFlight = Promise.resolve()
      .then(() => this.authenticate())
      .catch(() => false)
      .finally(() => {
        this.authenticationInFlight = null;
      });
  }

  getSessionVersion(): number {
    return this.sessionVersion;
  }

  private markSessionChanged(): void {
    this.sessionVersion += 1;
    this.sessionChangesSubject.next();
  }

  private getSessionFingerprint(): string | null {
    const claims = this.oauthService.getIdentityClaims() as
      | Record<string, unknown>
      | null
      | undefined;
    if (!claims) return null;
    const subject = typeof claims['sub'] === 'string' ? claims['sub'] : '';
    const issuer = typeof claims['iss'] === 'string' ? claims['iss'] : '';
    const session =
      typeof claims['sid'] === 'string'
        ? claims['sid']
        : typeof claims['session_state'] === 'string'
        ? claims['session_state']
        : '';
    return subject ? [issuer, subject, session].join('|') : null;
  }
}
